/**
 * SectionCalibrationFooter — CEO calibration affordance below each section's prose.
 *
 * Authority:
 *   docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §14 (TC-DS-CAL-01..03)
 *   docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §15 — verbose UX states
 *
 * Token bindings (zero hex codes — all from ui-invariants.js):
 *   container         → SECTION_PANEL.adjustFooter
 *   buttons (ghost)   → ACTION_BUTTONS.rerun + ACTION_BUTTONS.hover
 *   button labels     → DS_TYPOGRAPHY.actionBtn
 *   accepted stamp    → DS_COLORS.text.brand + DS_COLORS.surface.warm + DS_TYPOGRAPHY.badgeText
 *   overridden stamp  → VERDICT_PILL.moderate + DS_TYPOGRAPHY.badgeText
 *   reason / undo     → DS_TYPOGRAPHY.sectionSubLabel + DS_COLORS.text.secondary
 *   spacing           → DS_SPACING.{cardPadding, buttonGap, minTouchTarget}
 *   loading state     → UI_BUTTON_STATE.LOADING
 *
 * Strings: AGENT_CONFIG.deal_evaluator.calibrationFooter from task-display-config.js.
 */

import React, { useState } from 'react';
import {
  ACTION_BUTTONS,
  DS_BORDERS,
  DS_COLORS,
  DS_SPACING,
  DS_TYPOGRAPHY,
  SECTION_PANEL,
  UI_BUTTON_STATE,
  VERDICT_PILL,
} from '../../config/ui-invariants';
import { AGENT_CONFIG } from '../../config/task-display-config';
import { overrideSectionVerdict, undoSectionOverride } from '../../lib/api';
import VerdictOverridePopover from './VerdictOverridePopover';

const FOOTER_CFG = AGENT_CONFIG.deal_evaluator.calibrationFooter;

const ghostButtonStyle = {
  background: ACTION_BUTTONS.rerun.background,
  color: ACTION_BUTTONS.rerun.color,
  border: ACTION_BUTTONS.rerun.border,
  borderRadius: ACTION_BUTTONS.rerun.borderRadius,
  minHeight: ACTION_BUTTONS.rerun.minHeight,
  paddingLeft: '14px',
  paddingRight: '14px',
  cursor: 'pointer',
  ...DS_TYPOGRAPHY.actionBtn,
  transition: 'border-color 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
};

const ghostHoverEnter = (e) => {
  e.currentTarget.style.borderColor = ACTION_BUTTONS.hover.border;
  e.currentTarget.style.color = ACTION_BUTTONS.hover.text;
};
const ghostHoverLeave = (e) => {
  e.currentTarget.style.borderColor = DS_COLORS.border.default;
  e.currentTarget.style.color = ACTION_BUTTONS.rerun.color;
};

const renderInterpolated = (template, values) =>
  Object.entries(values).reduce(
    (acc, [k, v]) => acc.replace(`{${k}}`, v ?? ''),
    template,
  );

export default function SectionCalibrationFooter({
  startupId,
  sectionId,
  agentVerdict,
  ceoVerdictOverride,
  ceoVerdictOverrideReason,
  onOverrideCommitted,
  onRefineProseClick,
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [savingState, setSavingState] = useState('idle'); // idle | saving | error
  const [errorMessage, setErrorMessage] = useState(null);

  const isOverridden = Boolean(ceoVerdictOverride) && ceoVerdictOverride !== agentVerdict;
  const isAccepted = Boolean(ceoVerdictOverride) && ceoVerdictOverride === agentVerdict;
  const isIdle = !isOverridden && !isAccepted;

  const isSaving = savingState === 'saving';

  const handleAccept = async () => {
    if (isSaving) return;
    setSavingState('saving');
    setErrorMessage(null);
    try {
      const response = await overrideSectionVerdict(startupId, sectionId, {
        verdict: agentVerdict,
        override_source: 'direct_override',
      });
      onOverrideCommitted?.({
        ceo_verdict_override: response.ceo_verdict || agentVerdict,
        ceo_verdict_override_reason: null,
        ceo_verdict_overridden_at: response.overridden_at,
      });
      setSavingState('idle');
    } catch (err) {
      setSavingState('error');
      setErrorMessage(FOOTER_CFG.toasts.error);
    }
  };

  const handleOverrideConfirm = async ({ verdict, reason }) => {
    if (isSaving) return;
    setSavingState('saving');
    setErrorMessage(null);
    try {
      const response = await overrideSectionVerdict(startupId, sectionId, {
        verdict,
        ...(reason ? { reason } : {}),
        override_source: 'direct_override',
      });
      onOverrideCommitted?.({
        ceo_verdict_override: response.ceo_verdict || verdict,
        ceo_verdict_override_reason: reason || null,
        ceo_verdict_overridden_at: response.overridden_at,
      });
      setPopoverOpen(false);
      setSavingState('idle');
    } catch (err) {
      setSavingState('error');
      setErrorMessage(FOOTER_CFG.toasts.error);
    }
  };

  const handleUndo = async () => {
    if (isSaving) return;
    // WI-4: reverse the override in the backend (clears startup override fields +
    // marks the learning_signals row as undo_reverted) before updating local state.
    // Non-fatal: if the API call fails, we still clear local state so the UI is
    // consistent (the backend write will be out of sync, but the CEO can re-accept).
    try {
      await undoSectionOverride(startupId, sectionId);
    } catch (_undoErr) {
      // Non-fatal: undo the local state regardless so the UI doesn't stay stuck.
    }
    onOverrideCommitted?.({
      ceo_verdict_override: null,
      ceo_verdict_override_reason: null,
      ceo_verdict_overridden_at: null,
    });
  };

  const containerStyle = {
    background: SECTION_PANEL.adjustFooter.background,
    borderTop: SECTION_PANEL.adjustFooter.borderTop,
    padding: SECTION_PANEL.adjustFooter.padding,
    display: 'flex',
    flexDirection: 'column',
    gap: `${DS_SPACING.buttonGap}px`,
  };

  const buttonRowStyle = {
    display: 'flex',
    gap: `${DS_SPACING.buttonGap}px`,
    alignItems: 'center',
    flexWrap: 'wrap',
  };

  const errorStyle = {
    ...DS_TYPOGRAPHY.sectionSubLabel,
    color: DS_COLORS.error.color,
  };

  // Stamp shape (shared between accepted + overridden)
  const stampBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: DS_BORDERS.radiusPill,
    borderWidth: '1px',
    borderStyle: 'solid',
    ...DS_TYPOGRAPHY.badgeText,
  };

  const acceptedStampStyle = {
    ...stampBaseStyle,
    background: DS_COLORS.surface.warm,
    color: DS_COLORS.text.brand,
    borderColor: DS_COLORS.border.brand,
  };

  const overriddenStampStyle = {
    ...stampBaseStyle,
    background: VERDICT_PILL.moderate.background,
    color: VERDICT_PILL.moderate.text,
    borderColor: VERDICT_PILL.moderate.border,
  };

  const undoLinkStyle = {
    ...DS_TYPOGRAPHY.sectionSubLabel,
    color: DS_COLORS.text.secondary,
    background: 'none',
    border: 'none',
    padding: '4px 6px',
    cursor: 'pointer',
    textDecoration: 'underline',
    minHeight: `${DS_SPACING.minTouchTarget}px`,
  };

  const reasonStyle = {
    ...DS_TYPOGRAPHY.sectionSubLabel,
    color: DS_COLORS.text.secondary,
  };

  const loadingButtonStyle = isSaving
    ? {
        opacity: UI_BUTTON_STATE.LOADING.opacity,
        cursor: UI_BUTTON_STATE.LOADING.cursor,
        pointerEvents: UI_BUTTON_STATE.LOADING.pointerEvents,
      }
    : {};

  return (
    <div style={containerStyle} data-testid="section-calibration-footer">
      {isIdle && (
        <div style={buttonRowStyle}>
          <button
            type="button"
            onClick={handleAccept}
            onMouseEnter={ghostHoverEnter}
            onMouseLeave={ghostHoverLeave}
            disabled={isSaving}
            style={{ ...ghostButtonStyle, ...loadingButtonStyle }}
            data-testid="cal-accept-button"
          >
            {FOOTER_CFG.acceptButton.label}
          </button>
          <VerdictOverridePopover
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            onConfirm={handleOverrideConfirm}
            isSaving={isSaving}
            currentAgentVerdict={agentVerdict}
            triggerNode={
              <button
                type="button"
                onMouseEnter={ghostHoverEnter}
                onMouseLeave={ghostHoverLeave}
                disabled={isSaving}
                style={{ ...ghostButtonStyle, ...loadingButtonStyle }}
                data-testid="cal-override-button"
              >
                {FOOTER_CFG.overrideButton.label}
              </button>
            }
          />
          <button
            type="button"
            onClick={onRefineProseClick}
            onMouseEnter={ghostHoverEnter}
            onMouseLeave={ghostHoverLeave}
            disabled={isSaving}
            style={{ ...ghostButtonStyle, ...loadingButtonStyle }}
            data-testid="cal-refine-button"
          >
            {FOOTER_CFG.refineProseButton.label}
          </button>
        </div>
      )}

      {isAccepted && (
        <div style={buttonRowStyle}>
          <span style={acceptedStampStyle} data-testid="cal-accepted-stamp">
            <span aria-hidden="true">✓</span>
            <span>{FOOTER_CFG.acceptedStamp.label}</span>
          </span>
          <button
            type="button"
            onClick={handleUndo}
            disabled={isSaving}
            style={undoLinkStyle}
            data-testid="cal-undo-link"
          >
            {FOOTER_CFG.undoLink.label}
          </button>
        </div>
      )}

      {isOverridden && (
        <>
          <div style={buttonRowStyle}>
            <span style={overriddenStampStyle} data-testid="cal-overridden-stamp">
              <span aria-hidden="true">→</span>
              <span>
                {renderInterpolated(FOOTER_CFG.overriddenStampTemplate, {
                  ceo_verdict: capitalize(ceoVerdictOverride),
                  agent_verdict: capitalize(agentVerdict),
                })}
              </span>
            </span>
            <VerdictOverridePopover
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
              onConfirm={handleOverrideConfirm}
              isSaving={isSaving}
              currentAgentVerdict={agentVerdict}
              currentCeoVerdict={ceoVerdictOverride}
              currentReason={ceoVerdictOverrideReason}
              triggerNode={
                <button
                  type="button"
                  onMouseEnter={ghostHoverEnter}
                  onMouseLeave={ghostHoverLeave}
                  disabled={isSaving}
                  style={{
                    ...ghostButtonStyle,
                    ...loadingButtonStyle,
                    minHeight: `${DS_SPACING.minTouchTarget}px`,
                    paddingLeft: '12px',
                    paddingRight: '12px',
                  }}
                  data-testid="cal-edit-override"
                  aria-label={FOOTER_CFG.editButton.ariaLabel}
                >
                  ✎ {FOOTER_CFG.editButton.label}
                </button>
              }
            />
            <button
              type="button"
              onClick={handleUndo}
              disabled={isSaving}
              style={undoLinkStyle}
              data-testid="cal-undo-link-overridden"
            >
              {FOOTER_CFG.undoLink.label}
            </button>
          </div>
          {ceoVerdictOverrideReason && (
            <div style={reasonStyle} data-testid="cal-override-reason">
              {ceoVerdictOverrideReason}
            </div>
          )}
        </>
      )}

      {savingState === 'error' && errorMessage && (
        <div style={errorStyle} role="alert" data-testid="cal-error">
          {errorMessage}
        </div>
      )}
    </div>
  );
}

function capitalize(str) {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}
